import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import PartnerInfoSwitch from './PartnerInfoSwitch';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import englishLanguageTest from '../../../data/selectInputOptions/englishLanguageTest.json';
import frenchLanguageTest from '../../../data/selectInputOptions/frenchLanguageTest.json';
import api from 'api';
import { formCounterSlice } from '../../../redux/formCounter';
import { useDispatch, useSelector } from 'react-redux';
import { Spouse } from './GeneralInfo';
import languages from '../../../data/selectInputOptions/languageList.json';
import ReactGA from 'react-ga4';
import * as S from '../Forms.styled';
import { CustomSlider } from 'components/common/CustomSlider/CustomSlider';
import { getLanguageScoresNewFormat as getScores } from '../../../utils/options';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import { isSalesPersonRole } from 'data/constants/roles';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

const LANGUAGES = Object.values(languages).map((item) => item.name);

interface Entry {
    language: string | null;
    listening: string | null;
    reading: string | null;
    speaking: string | null;
    writing: string | null;
    test_type: string;
    level: string | null | number;
}

const initEnglish: Entry = {
    language: 'English',
    listening: '',
    reading: '',
    speaking: '',
    writing: '',
    test_type: '',
    level: null,
};

interface Msg {
    language: string;
    listening: string;
    reading: string;
    speaking: string;
    writing: string;
    test_type: string;
    level: string;
}

const initMsg: Msg = {
    language: '',
    listening: '',
    reading: '',
    speaking: '',
    writing: '',
    test_type: '',
    level: '',
};

const initNative: Entry = {
    language: '',
    listening: '9',
    reading: '9',
    speaking: '9',
    writing: '9',
    test_type: 'Self assessment',
    level: 5,
};

const initFrench: Entry = {
    language: 'French',
    listening: '',
    reading: '',
    speaking: '',
    writing: '',
    test_type: '',
    level: null,
};

/** Native rows are saved with level 5; API may return 5, "5", or omit it on older records. */
const isNativeLevel = (level: Entry['level']) => Number(level) === 5;

const initialMarks = [
    { value: 0, label: '0' },
    { value: 9, label: '9' },
];

export const CEFR_SCALE: Record<number, string> = {
    6: 'C2',
    5: 'C1',
    4: 'B2',
    3: 'B1',
    2: 'A2',
    1: 'A1',
};

export default function LanguageFormV2() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasCanada } = useEnabledCountries();
    const tableName = 'Language';
    const [data, setData] = useState<Entry[]>([initNative, initEnglish]);
    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([initMsg, initMsg]);
    const [speaksFrench, setSpeaksFrench] = useState<boolean>(false);
    const { setShowSpouse } = formCounterSlice.actions;
    const dispatch = useDispatch();
    const [, setHasSpouse] = useState<boolean>(false);
    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [showEnglish, setShowEnglish] = useState<boolean>(true);
    const [showFrench, setShowFrench] = useState<boolean>(false);
    const [currentNativeLanguage, setCurrentNativeLanguage] = useState<string>('');
    const [frenchIdx, setFrenchIdx] = useState<number>(2);

    const getLanguageIdx = (entries: Entry[], language: string) => {
        return entries.findIndex((x) => x.language === language);
    };

    useEffect(() => {
        if (isSpouseInfo && isUserData) {
            setIsLoading(false);
        }
    }, [isSpouseInfo, isUserData]);

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'LanguageForm',
        });
    }, []);

    const loadData = (val: Entry[]) => {
        if (val && val.length | Object.keys(val).length) {
            const nativeSource = val.find((x) => isNativeLevel(x.level)) ?? val[0];
            const nativeLanguage = nativeSource ? { ...nativeSource, level: 5 } : initNative;
            const english = val.find((x) => x.language === 'English' && x !== nativeSource);
            if (english?.test_type) {
                handleUpdate(1, 'test_type', english?.test_type);
            }
            const french = val.find((x) => x.language === 'French' && x !== nativeSource);
            if (french?.test_type) {
                handleUpdate(2, 'test_type', french?.test_type);
            }
            let tmp: Entry[] = [];

            if (nativeLanguage.language === 'English') {
                tmp = [nativeLanguage];
                setShowEnglish(false);
                setCurrentNativeLanguage('English');
                setShowFrench(true);
                if (french) {
                    setSpeaksFrench(true);
                    tmp.push(french);
                    setFrenchIdx(1);
                }
            } else if (nativeLanguage.language === 'French') {
                tmp = [nativeLanguage, english ?? initEnglish];
                setShowFrench(false);
                setCurrentNativeLanguage('French');
                setShowEnglish(true);
            } else {
                tmp = [nativeLanguage];
                tmp.push(english ?? initEnglish);
                setShowEnglish(true);
                setShowFrench(true);
                setCurrentNativeLanguage(tmp[0].language ? tmp[0].language : '');

                if (french) {
                    setSpeaksFrench(true);
                    tmp.push(french);
                    setFrenchIdx(2);
                } else setSpeaksFrench(false);
            }
            const tmpValMsg = new Array(tmp.length).fill(initMsg);
            setValidationMsg(tmpValMsg);
            setData(tmp);
        }
        if (val == null || val) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    const handleSpouse = async (data: Spouse) => {
        if (data && Object.keys(data).length) {
            setHasSpouse(true);
        }

        if (data == null || data) {
            setIsSpouseInfo(true);
        } else {
            setIsSpouseInfo(false);
        }
    };

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);

    const isSalesPerson = isSalesPersonRole(role);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setIsSpouseInfo(false);
            setData([initNative, initEnglish]);
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(loadData, tableName, candidate_id);
                api.tableInput.fetchCandidateInputSales(handleSpouse, 'Spouse', candidate_id);
            } else {
                api.tableInput.fetchCandidateInput(loadData, tableName);
                api.tableInput.fetchCandidateInput(handleSpouse, 'Spouse');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, isAuthenticated]);

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            for (const key of Object.keys(initNative)) {
                const value = data[i][key as keyof Entry];
                let msg = '';
                if (
                    value === ''
                    //|| value === null
                ) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const [marks, setMarks] = useState(initialMarks);
    const [scores, setScores] = useState({ minScore: 0, maxScore: 9 });
    const [frenchScores, setFrenchScores] = useState({ minScore: 0, maxScore: 9 });
    const [frenchMarks, setFrenchMarks] = useState(initialMarks);

    const handleUpdate = (idx: number, field: string, value: string | null) => {
        const temp = [...data];
        if (field === 'test_type') {
            temp[idx] = {
                ...data[idx],
                [field]: value ? value : '',
                speaking: '',
                listening: '',
                reading: '',
                writing: '',
            };
            //English
            if (idx === 1) {
                if (value) {
                    const tempScore = getScores(value);
                    if (frenchIdx === 1) {
                        setFrenchScores({
                            maxScore: Number(tempScore[getScores(value).length - 1]),
                            minScore: Number(tempScore[0]),
                        });
                    } else {
                        setScores({
                            maxScore: Number(tempScore[getScores(value).length - 1]),
                            minScore: Number(tempScore[0]),
                        });
                    }
                    const tempMark = [...marks];
                    tempMark[1] = {
                        value: Number(tempScore[getScores(value).length - 1]),
                        label: Number(tempScore[getScores(value).length - 1]).toString(),
                    };
                    if (frenchIdx === 1) {
                        setFrenchMarks(tempMark);
                    } else {
                        setMarks(tempMark);
                    }
                }
            } else {
                if (value) {
                    const tempScore = getScores(value);
                    setFrenchScores({
                        maxScore: Number(tempScore[getScores(value).length - 1]),
                        minScore: Number(tempScore[0]),
                    });
                    const tempMark = [...frenchMarks];
                    tempMark[1] = {
                        value: Number(tempScore[getScores(value).length - 1]),
                        label: Number(tempScore[getScores(value).length - 1]).toString(),
                    };
                    setFrenchMarks(tempMark);
                }
            }
        } else {
            temp[idx] = { ...data[idx], [field]: value };
        }
        setData(temp);
    };

    const handleUpdateSpeaksFrench = (value: string | null) => {
        let tmp = [...data];
        if (value === t('yes', { lng: language }) && !speaksFrench) {
            setSpeaksFrench(true);
            setShowFrench(true);
            tmp = [...tmp, initFrench];
            setFrenchIdx(getLanguageIdx(tmp, 'French'));
        } else if (value === t('no', { lng: language }) && speaksFrench) {
            setSpeaksFrench(false);
            tmp = tmp.filter((x) => x.language !== 'French');
        }
        const tmpValMsg = new Array(tmp.length).fill(initMsg);
        setValidationMsg(tmpValMsg);
        setData(tmp);
    };

    function handleSwitchPartnerMe() {
        if (validate()) {
            if (isSalesPerson) {
                api.tableInput.postSales(data, tableName, candidate_id);
            } else {
                api.tableInput.post(data, tableName);
            }
            dispatch(setShowSpouse());
        }
    }

    function handleMotherLanguage(val: string | null) {
        let tmp: Entry[] = [];
        if (val === 'English' && currentNativeLanguage !== 'English') {
            const english: Entry = {
                ...initNative,
                language: 'English',
                level: 5,
            };
            tmp = [english];
            const french = data.find((x) => x.language === 'French');

            if (french) {
                french.level = null; // distinguish from native languge (level = 5)
                tmp.push(french);
            }
            setShowEnglish(false);
            setShowFrench(true);
            setFrenchIdx(1);
            setCurrentNativeLanguage('English');
            setSpeaksFrench(Boolean(french));
        } else if (val === 'French' && currentNativeLanguage !== 'French') {
            const english = data.find((x) => x.language === 'English');
            if (english) english.level = null; // distinguish from native languge (level = 5)

            const french: Entry = {
                ...initNative,
                language: 'French',
                level: 5,
            };
            setShowFrench(false);
            setSpeaksFrench(false);
            setCurrentNativeLanguage('French');
            setShowEnglish(true);
            tmp = [french, english ? english : initEnglish];
        } else {
            const english = data.find((x) => x.language === 'English');
            if (english) english.level = null; // distinguish from native languge (level = 5)
            const french = data.find(
                (x) => x.language === 'French' && currentNativeLanguage !== 'French'
            );
            const native: Entry = {
                ...initNative,
                language: val,
                level: 5,
            };
            tmp = [native, english ? english : initEnglish];
            if (french) {
                tmp.push(french);
            }
            setShowFrench(true);
            setShowEnglish(true);
            setCurrentNativeLanguage(String(val));
            setFrenchIdx(2);
        }
        const tempMsg: Msg[] = new Array(tmp.length).fill(initMsg);
        setValidationMsg(tempMsg);
        setData(tmp);
    }
    //for IELTS
    const [stepOutside, setStepOutside] = useState(1);

    function formatStep(value: number | number[]) {
        if (data[1]?.test_type === 'IELTS' && typeof value === 'number' && value > 4) {
            setStepOutside(0.5);
        } else {
            setStepOutside(1);
        }
    }

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={[tableName]}>
                    <S.TitleContainer>
                        <S.Title>{t('languageProficiency', { lng: language })}</S.Title>
                        <PartnerInfoSwitch partnerActive={false} onSwitch={handleSwitchPartnerMe} />
                    </S.TitleContainer>
                    <Autocomplete
                        sx={{
                            '& .MuiInputBase-input': {
                                height: 30,
                            },
                            marginTop: 4,
                        }}
                        disablePortal
                        id="language"
                        value={data[0].language}
                        options={LANGUAGES}
                        onChange={(_, v: string | null) => {
                            handleMotherLanguage(v);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`*${t('nativeLanguage', { lng: language })}`}
                                error={validationMsg[0].language !== ''}
                                // helperText={LngMsg.language}
                            />
                        )}
                    />
                    <S.ValidationMsg>{validationMsg[0].language}</S.ValidationMsg>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '15px',
                        }}
                    >
                        {showEnglish && (
                            <S.EntryCard>
                                <S.LanguageTitle>{t('english', { lng: language })}</S.LanguageTitle>
                                <S.AssessmentQuestion>
                                    {t('takenOfficialTest', { lng: language })}
                                </S.AssessmentQuestion>
                                {/* <S.AssessmentLink>
                                    <button
                                        onClick={() =>
                                            window.open('/language_self_assessment', '_blank')
                                        }>
                                        {t('clickHereSelfAssessment', { lng: language })}
                                    </button>
                                </S.AssessmentLink> */}
                                <S.SliderGridSection>
                                    <S.FlexColumn>
                                        <Autocomplete
                                            id="test_type"
                                            value={data[1]?.test_type}
                                            options={englishLanguageTest}
                                            onChange={(_, v: string | null) => {
                                                handleUpdate(1, 'test_type', v);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={`*${t('testType', { lng: language })}`}
                                                    error={validationMsg[1]?.test_type !== ''}
                                                />
                                            )}
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[1]?.test_type}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                    <S.FlexColumn></S.FlexColumn>
                                    <S.FlexColumn>
                                        <p>{t('listening', { lng: language })}</p>
                                        <CustomSlider
                                            id="test_score"
                                            value={parseFloat(data[1]?.listening || '0')}
                                            onChange={(_, value) => {
                                                formatStep(value);

                                                handleUpdate(1, 'listening', value.toString());
                                            }}
                                            min={scores.minScore}
                                            max={scores.maxScore}
                                            marks={marks}
                                            step={stepOutside}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={(value) =>
                                                data[1]?.test_type === 'CEFR'
                                                    ? CEFR_SCALE[value] || value
                                                    : value
                                            }
                                            aria-labelledby="test-score-slider"
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[1]?.listening}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                    <S.FlexColumn>
                                        <p>{t('reading', { lng: language })}</p>
                                        <CustomSlider
                                            id="test_score"
                                            value={parseFloat(data[1]?.reading || '0')}
                                            onChange={(_, value) => {
                                                formatStep(value);
                                                handleUpdate(1, 'reading', value.toString());
                                            }}
                                            min={scores.minScore}
                                            max={scores.maxScore}
                                            marks={marks}
                                            step={stepOutside}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={(value) =>
                                                data[1]?.test_type === 'CEFR'
                                                    ? CEFR_SCALE[value] || value
                                                    : value
                                            }
                                            aria-labelledby="test-score-slider"
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[1]?.reading}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                    <S.FlexColumn>
                                        <p>{t('writing', { lng: language })}</p>
                                        <CustomSlider
                                            id="test_score"
                                            value={parseFloat(data[1]?.writing || '0')}
                                            onChange={(_, value) => {
                                                formatStep(value);
                                                handleUpdate(1, 'writing', value.toString());
                                            }}
                                            min={scores.minScore}
                                            max={scores.maxScore}
                                            marks={marks}
                                            step={stepOutside}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={(value) =>
                                                data[1]?.test_type === 'CEFR'
                                                    ? CEFR_SCALE[value] || value
                                                    : value
                                            }
                                            aria-labelledby="test-score-slider"
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[1]?.writing}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                    <S.FlexColumn>
                                        <p>{t('speaking', { lng: language })}</p>
                                        <CustomSlider
                                            id="test_score"
                                            value={parseFloat(data[1]?.speaking || '0')}
                                            onChange={(_, value) => {
                                                formatStep(value);
                                                handleUpdate(1, 'speaking', value.toString());
                                            }}
                                            min={scores.minScore}
                                            max={scores.maxScore}
                                            marks={marks}
                                            step={stepOutside}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={(value) =>
                                                data[1]?.test_type === 'CEFR'
                                                    ? CEFR_SCALE[value] || value
                                                    : value
                                            }
                                            aria-labelledby="test-score-slider"
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[1]?.speaking}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                </S.SliderGridSection>
                            </S.EntryCard>
                        )}
                        {hasCanada && showFrench && (
                            <>
                                <span>
                                    <ToggleButtonGroup
                                        options={[
                                            t('yes', { lng: language }),
                                            t('no', { lng: language }),
                                        ]}
                                        name="speak_french"
                                        label={t('doYouSpeakFrench', { lng: language })}
                                        value={
                                            speaksFrench
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })
                                        }
                                        onChange={(_: string, value: string) =>
                                            handleUpdateSpeaksFrench(value)
                                        }
                                    />
                                </span>
                                {speaksFrench && (
                                    <S.EntryCard>
                                        <S.LanguageTitle>
                                            {t('french', { lng: language })}
                                        </S.LanguageTitle>
                                        <div style={{ marginTop: 7, marginBottom: 16 }}>
                                            <S.AssessmentQuestion>
                                                {t('takenOfficialTest', { lng: language })}
                                            </S.AssessmentQuestion>
                                            {/* <S.AssessmentLink>
                                                <button
                                                    onClick={() =>
                                                        window.open(
                                                            '/language_self_assessment',
                                                            '_blank'
                                                        )
                                                    }>
                                                    {t('clickHereSelfAssessment', {
                                                        lng: language,
                                                    })}
                                                </button>
                                            </S.AssessmentLink> */}
                                        </div>
                                        {/* {data[0].language !== 'French' && ( */}
                                        <S.SliderGridSection>
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    id="test_type_fr"
                                                    value={data[frenchIdx].test_type}
                                                    options={frenchLanguageTest}
                                                    onChange={(_, v: string | null) =>
                                                        handleUpdate(frenchIdx, 'test_type', v)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('testType', {
                                                                lng: language,
                                                            })}`}
                                                            error={
                                                                validationMsg[frenchIdx]
                                                                    ?.test_type !== ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[frenchIdx]?.test_type}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn />

                                            <S.FlexColumn>
                                                <p>{t('listening', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Listening"
                                                    value={parseFloat(
                                                        data[frenchIdx].listening || '0'
                                                    )}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            frenchIdx,
                                                            'listening',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={frenchScores.minScore}
                                                    max={frenchScores.maxScore}
                                                    marks={frenchMarks}
                                                    step={1}
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) => value}
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[frenchIdx].listening}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('reading', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Reading"
                                                    value={parseFloat(
                                                        data[frenchIdx].reading || '0'
                                                    )}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            frenchIdx,
                                                            'reading',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={frenchScores.minScore}
                                                    max={frenchScores.maxScore}
                                                    marks={frenchMarks}
                                                    step={1}
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) => value}
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[frenchIdx].reading}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('writing', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Writing"
                                                    value={parseFloat(
                                                        data[frenchIdx].writing || '0'
                                                    )}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            frenchIdx,
                                                            'writing',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={frenchScores.minScore}
                                                    max={frenchScores.maxScore}
                                                    marks={frenchMarks}
                                                    step={1}
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) => value}
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[frenchIdx].writing}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                            <S.FlexColumn>
                                                <p>{t('speaking', { lng: language })}</p>
                                                <CustomSlider
                                                    id="Speaking"
                                                    value={parseFloat(
                                                        data[frenchIdx].speaking || '0'
                                                    )}
                                                    onChange={(_, value) => {
                                                        formatStep(value);
                                                        handleUpdate(
                                                            frenchIdx,
                                                            'speaking',
                                                            value.toString()
                                                        );
                                                    }}
                                                    min={frenchScores.minScore}
                                                    max={frenchScores.maxScore}
                                                    marks={frenchMarks}
                                                    step={1}
                                                    valueLabelDisplay="on"
                                                    valueLabelFormat={(value) => value}
                                                    aria-labelledby="test-score-slider"
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[frenchIdx].speaking}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        </S.SliderGridSection>
                                    </S.EntryCard>
                                )}
                            </>
                        )}
                    </div>
                </FormBox>
            )}
        </>
    );
}
